import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Achievement } from '../progress/AchievementsSection';
import { CollectibleAchievementCard, AchievementCard } from '../progress/AchievementsSection';
import { getAchievementIllustration, DjayAvatar } from '../../mascot';
import { VariantUnlockInfo } from '../../mascot/useVariantUnlocks';
interface AchievementCategoryGroupProps {
  title: string;
  achievements: (Achievement & {
    isUnlocked: boolean;
    progress?: {
      current: number;
      max: number;
      label: string;
    };
  })[];
  mascotVariants: VariantUnlockInfo[];
}
export function AchievementCategoryGroup({
  title,
  achievements,
  mascotVariants
}: AchievementCategoryGroupProps) {
  // Sort: Unlocked first, then by progress percentage descending
  const sortedAchievements = useMemo(() => {
    return [...achievements].sort((a, b) => {
      if (a.isUnlocked && !b.isUnlocked) return -1;
      if (!a.isUnlocked && b.isUnlocked) return 1;
      const pctA = a.progress && a.progress.max > 0 ? a.progress.current / a.progress.max : 0;
      const pctB = b.progress && b.progress.max > 0 ? b.progress.current / b.progress.max : 0;
      return pctB - pctA;
    });
  }, [achievements]);
  const unlockedCount = achievements.filter(a => a.isUnlocked).length;
  if (achievements.length === 0) return null;
  return <div className="space-y-4" data-id="element-1092">
      <div className="flex items-center justify-between border-b-2 border-slate-100 pb-2" data-id="element-1093">
        <h3 className="text-lg font-black text-slate-800" data-id="element-1094">{title}</h3>
        <span className="text-sm font-bold text-slate-400 bg-slate-100 px-3 py-1 rounded-full" data-id="element-1095">
          {unlockedCount} / {achievements.length}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-1096">
        <AnimatePresence mode="popLayout" data-id="element-1097">
          {sortedAchievements.map((achievement, index) => {
          const Illustration = getAchievementIllustration(achievement.id);
          const variantInfo = achievement.djayVariantUnlocked ? mascotVariants.find(v => v.id === achievement.djayVariantUnlocked) : null;
          return <motion.div key={achievement.id} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.2
          }} className="relative flex flex-col h-full" data-id="element-1098">
                <div className="flex-1" data-id="element-1099">
                  {Illustration ? <CollectibleAchievementCard achievement={achievement} isUnlocked={achievement.isUnlocked} index={index} compact={false} data-id="element-1100" /> : <AchievementCard achievement={achievement} isUnlocked={achievement.isUnlocked} index={index} data-id="element-1101" />}
                </div>

                {/* Bonus Reward Pill */}
                {achievement.djayVariantUnlocked && variantInfo && <div className={`mt-2 flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 ${achievement.isUnlocked ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-1102">
                    <div className={`w-8 h-8 flex-shrink-0 overflow-hidden rounded-full bg-white border border-slate-200 flex items-center justify-center ${!achievement.isUnlocked ? 'grayscale opacity-50' : ''}`} data-id="element-1103">
                      <div className="transform scale-75 mt-2" data-id="element-1104">
                        <DjayAvatar size="sm" emotion="happy" variant={achievement.djayVariantUnlocked} animate={false} data-id="element-1105" />
                      </div>
                    </div>
                    <div className="flex flex-col" data-id="element-1106">
                      <span className={`text-[9px] font-bold uppercase tracking-wider ${achievement.isUnlocked ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-1107">
                        {achievement.isUnlocked ? 'Ontgrendeld:' : 'Beloning:'}
                      </span>
                      <span className={`text-xs font-black ${achievement.isUnlocked ? 'text-orange-900' : 'text-slate-500'}`} data-id="element-1108">
                        {variantInfo.name}
                      </span>
                    </div>
                  </div>}
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </div>;
}